import type { ComponentChildren } from 'preact';

import type { IsolatedKey } from 'Addons/fitmentSearch/isolatedKeys';
import type { SelectionEntry } from 'Models/index';
import type { ServerModel } from 'Modules/serverApi/types';

interface BaseWidget<T extends string> {
  name: string;
  type: T;
  visible: boolean;
  isRoot: boolean;
  element: HTMLElement | null;
  component?: () => ComponentChildren;
  needInitRequest?: boolean;
  hasSearchResults?: boolean;
  consideredInScroll?: boolean;
  [key: string]: unknown;
}

interface RootWidget<T extends string> extends BaseWidget<T> {
  location: string;
  template: string | (() => ComponentChildren);
  isRoot: true;
}

type BaseOrRootWidget<T extends string> = BaseWidget<T> | RootWidget<T>;

interface FixedLocationWidget<T extends string> extends BaseWidget<T> {
  template: string;
  isRoot: true;
}

/**
 * Single source of truth for every widget kind: maps the discriminator
 * string to its full shape. `AnyWidget`, `WidgetTypes`, and
 * `widgetTypeNames` are all derived from this map, so adding a new
 * widget is one entry here and the union, the runtime registry, and
 * every `Widget<'Foo'>` lookup all update in lockstep.
 */
interface WidgetShapeRegistry {
  Comparer: FixedLocationWidget<'Comparer'>;
  ContextDialog: RootWidget<'ContextDialog'> & { closeOnClickOutside?: boolean };
  DialogButton: BaseWidget<'DialogButton'>;
  FacetBar: BaseWidget<'FacetBar'> & { fields?: string[] };
  FacetPanel: BaseWidget<'FacetPanel'> & {
    fields?: string[];
    ignoreFields?: string[];
  };
  FacetToggle: BaseWidget<'FacetToggle'>;
  FilterChips: BaseWidget<'FilterChips'> & {
    fields?: string[];
    ignoreFields?: string[];
  };
  FilterSearchBox: BaseWidget<'FilterSearchBox'>;
  SearchPage: RootWidget<'SearchPage'>;
  SearchBox: BaseOrRootWidget<'SearchBox'>;
  SearchBoxDialogButton: BaseOrRootWidget<'SearchBoxDialogButton'>;
  SearchHeader: BaseWidget<'SearchHeader'>;
  SearchResult: BaseWidget<'SearchResult'>;
  VehicleWidget: BaseOrRootWidget<'VehicleWidget'> & {
    isolatedKey: IsolatedKey;
    isAlwaysActive: boolean;
    isVehicleSelectionIsolated?: boolean;
    fields?: string[];
  };
  VehicleTabs: BaseOrRootWidget<'VehicleTabs'>;
  FacetTiles: BaseWidget<'FacetTiles'> & { isVehicleWidgetDisabled?: boolean };
  VehicleTiles: BaseWidget<'VehicleTiles'>;
  VerifyFitment: BaseWidget<'VerifyFitment'>;
  FitmentTable: BaseWidget<'FitmentTable'>;
  Garage: BaseWidget<'Garage'>;
  SingleVehicleGarage: BaseWidget<'SingleVehicleGarage'>;
  RequestPanel: BaseWidget<'RequestPanel'> & {
    selectFields?: string[];
    requiredFields?: string[];
  };
  RelatedItems: BaseWidget<'RelatedItems'> & {
    getSelection?: (itemData: ServerModel.SearchItem) => SelectionEntry[] | undefined;
  };
  ProductData: BaseWidget<'ProductData'>;
  RelatedColors: BaseWidget<'RelatedColors'>;
  BuyItemsTogether: BaseWidget<'BuyItemsTogether'> & {
    mode?: string;
    count?: number;
    onItemsRendered?: () => void;
    initSelected?: boolean;
  };
  FacetDialog: BaseWidget<'FacetDialog'> & {
    fields?: string[];
    ignoreFields?: string[];
  };
  SearchBoxDialog: BaseWidget<'SearchBoxDialog'>;
  VehicleWidgetDialog: BaseWidget<'VehicleWidgetDialog'> & { fields?: string[] };
  SimpleColorPaletteDialog: BaseWidget<'SimpleColorPaletteDialog'>;
  ColorExtractorDialog: BaseWidget<'ColorExtractorDialog'>;
  VehicleInfo: BaseWidget<'VehicleInfo'>;
  WheelTireVehicleInfo: BaseWidget<'WheelTireVehicleInfo'>;
  AutoSyncVisualization: BaseWidget<'AutoSyncVisualization'>;
  AutoSyncVisualizationDialog: BaseWidget<'AutoSyncVisualizationDialog'>;
  Dialog: BaseWidget<'Dialog'>;
}

export type WidgetTypes = keyof WidgetShapeRegistry & string;
export type AnyWidget = WidgetShapeRegistry[WidgetTypes];
export type Widget<T extends WidgetTypes> = WidgetShapeRegistry[T];

/**
 * Runtime-visible registry of every widget kind. The TypeScript
 * `WidgetShapeRegistry` above is erased at compile time, so a runtime
 * check that needs to know "is this string a known widget type?"
 * needs an explicit value to read keys off of.
 *
 * Bidirectional exhaustiveness:
 *   - `satisfies Record<WidgetTypes, true>` requires *every* widget
 *     kind from the registry to appear here (catches *missing* names).
 *   - The `_widgetTypeMapExhaustive` assertion below requires the keys
 *     to be exactly `WidgetTypes`, no more (catches *extra* names).
 *
 * Adding a new widget kind is one edit to `WidgetShapeRegistry` and
 * one matching key here; both directions are enforced by `tsc`.
 */
const widgetTypeMap = {
  Comparer: true,
  ContextDialog: true,
  DialogButton: true,
  FacetBar: true,
  FacetPanel: true,
  FacetToggle: true,
  FilterChips: true,
  FilterSearchBox: true,
  SearchPage: true,
  SearchBox: true,
  SearchBoxDialogButton: true,
  SearchHeader: true,
  SearchResult: true,
  VehicleWidget: true,
  VehicleTabs: true,
  FacetTiles: true,
  VehicleTiles: true,
  VerifyFitment: true,
  FitmentTable: true,
  Garage: true,
  SingleVehicleGarage: true,
  RequestPanel: true,
  RelatedItems: true,
  ProductData: true,
  RelatedColors: true,
  BuyItemsTogether: true,
  FacetDialog: true,
  SearchBoxDialog: true,
  VehicleWidgetDialog: true,
  SimpleColorPaletteDialog: true,
  ColorExtractorDialog: true,
  VehicleInfo: true,
  WheelTireVehicleInfo: true,
  AutoSyncVisualization: true,
  AutoSyncVisualizationDialog: true,
  Dialog: true,
} as const satisfies Record<WidgetTypes, true>;

type AssertEqual<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
const _widgetTypeMapExhaustive: AssertEqual<keyof typeof widgetTypeMap, WidgetTypes> = true;
void _widgetTypeMapExhaustive;

export const widgetTypeNames = Object.keys(widgetTypeMap) as readonly WidgetTypes[];
